import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, concat, Observable } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { DownloadItem } from './download-item';
import { FileEntry } from 'common/uploads/file-entry';
import { DownloadProgressEvent, DownloadCompletedEvent, DownloadEventTypes, DownloadEvent } from './download-progress-event';
import { BackendErrorResponse } from 'common/core/types/backend-error-response';
import { DownloadApiService } from './download-api.service';
import { Store } from '@ngxs/store';
import { DriveState } from '../../app/drive/state/drive-state';
import { DownloadEntries } from '../../app/drive/state/actions/commands';
import { Settings } from '../core/config/settings.service';
import { HttpClient } from '@angular/common/http';
import { ShareableLink } from '../../app/drive/sharing/links/models/shareable-link';

@Injectable({
    providedIn: 'root'
})
export class DownloadItemService {
    private download$: BehaviorSubject<DownloadItem> = new BehaviorSubject(null);
    private totalProgress$: BehaviorSubject<number> = new BehaviorSubject(0);
    private progress$: BehaviorSubject<DownloadEvent> = new BehaviorSubject(null);
    private api: DownloadApiService = null;

    constructor(
        private zone: NgZone,
        private store: Store,
        private settings: Settings,
        private http: HttpClient
    ) { }

    public get(): Observable<DownloadItem> {
        return this.download$.asObservable();
    }

    public updateTotalProgress() {
        const progress = this.download$.value.meta$.value.progress || 0;
        this.totalProgress$.next(progress);
    }

    public totalProgress() {
        return this.totalProgress$.asObservable();
    }

    public progress() {
        return this.progress$.asObservable();
    }

    public start(fileEntry: FileEntry, link: ShareableLink, password: string): Observable<FileEntry> {
        const download = new DownloadItem(fileEntry)
        this.download$.next(download);

        this.api = new DownloadApiService(this.http)
        const request = this.api.start(this.getChunkDownloadUrl(link, password), download.fileEntry).pipe(
            takeUntil(download.canceled$),
            tap(
                response => this.handleDownloadEvent(response, download),
                response => this.handleDownloadFailure(response, download),
            ),
        );

        return request
            .pipe(
                filter(e => e.name === DownloadEventTypes.COMPLETED),
                map((e: DownloadCompletedEvent) => e.fileEntry)
            );
    }

    public updateProgress(id: string, e: DownloadProgressEvent) {
        const queueItem = this.download$.value;
        if (!queueItem) return;

        queueItem.update({
            eta: e.eta,
            speed: e.speed,
            progress: e.progress,
            totalBytes: e.totalBytes,
            completedBytes: e.completedBytes,
        });

        this.updateTotalProgress();
    }

    public completeDownload(id: string, fileEntry: FileEntry, totalCachedCf: number, totalCachedEdge: number) {
        const queueItem = this.download$.value;
        if (!queueItem) return;

        queueItem.fileEntry = fileEntry;
        queueItem.totalCachedCf = totalCachedCf;
        queueItem.totalCachedEdge = totalCachedEdge;
        queueItem.complete();
    }

    public errorDownload(id: string, message: string = '') {
        this.download$.value.addError(message);
    }

    public abortDownload() {
        if (this.api) {
            if (this.api.getFileStream()) this.api.getFileStream().abort()
            if (this.api.getFileWriter()) this.api.getFileWriter().abort()
        }
    }

    public reset() {
        this.download$.next(null);
    }

    protected handleDownloadEvent(event: DownloadEvent, download: DownloadItem) {
        this.progress$.next(event)

        if (event.name === DownloadEventTypes.PROGRESS) {
            this.zone.run(() => {
                this.updateProgress(download.id, event);
            });
        } else if (event.name === DownloadEventTypes.COMPLETED) {
            this.zone.run(() => {
                this.completeDownload(download.id, event.fileEntry, event.totalCachedCf, event.totalCachedEdge);
            });
        }
    }

    protected handleDownloadFailure(response: BackendErrorResponse, download: DownloadItem) {
        const msg = response.messages ? response.messages.file : '';
        this.errorDownload(download.id, msg);
    }

    private getChunkDownloadUrl(link: ShareableLink, password: string): string {
        const fileEntry: FileEntry = link.entry
        const hashes = this.getEntryHashes(fileEntry);
        let base = `${this.settings.getBaseUrl()}secure/uploads/downloadChunk?hashes=${hashes}`;
        if (link) base += `&shareable_link=${link.id}`;
        if (password) base += `&password=${password}`;
        return base;
    }

    /**
     * Get hashes string for specified entries or for currently selected entries.
     */
    private getEntryHashes(entry?: FileEntry): string {
        if (!entry) {
            entry = this.store.selectSnapshot(DriveState.selectedEntries[0]);
        }

        return entry.hash
    }
}
